import {
  design,
  FactoryFieldSelect,
  FactoryHeader,
  FactoryParagraph,
  FactoryRow,
  FactorySpaced,
  FactoryFill,
  FactoryAnchor
} from '@webnuts/component-factories'

const COLORS = {
  tangerineYellow: { 500: '#FFCB05' },
  kahuBlue: { 500: '#0097DC' },
  grey: { 500: '#848F9B', 400: '#C1C7CD', 300: '#E0E3E6', 200: '#EFF1F2' },
  onLight: { 500: '#2D3844', 400: '#717C89', 300: '#848F9B', 200: '#C1C7CD', 100: '#E0E3E6' },
  onDark: { 500: '#FFFFFF' },
  hyperlinks: { 500: '#0097DC' }
} as const

const FONTS = {
  heading1: { family: "'PT Sans', sans-serif", size: '48px', lineHeight: '60px', weight: 700 },
  heading2: { family: "'PT Sans', sans-serif", size: '30px', lineHeight: '36px', weight: 700 },
  heading3: { family: "'PT Sans', sans-serif", size: '24px', lineHeight: '32px', weight: 700 },
  heading4: { family: "'PT Sans', sans-serif", size: '18px', lineHeight: '27px', weight: 700 },
  heading5: { family: "'PT Sans', sans-serif", size: '16px', lineHeight: '18px', weight: 700 },

  larger: { family: "'PT Sans', sans-serif", size: '18px', lineHeight: '27px', weight: 400 },
  large: { family: "'PT Sans', sans-serif", size: '16px', lineHeight: '24px', weight: 400 },
  normal: { family: "'PT Sans', sans-serif", size: '14px', lineHeight: '21px', weight: 400 }
} as const

export const GLOBALS = {
  colors: COLORS,
  fonts: FONTS,

  padding: {
    default: { top: '16px', bottom: '16px', left: '16px', right: '16px' }
  },

  borders: {},

  field: {
    label: {
      placement: 'none',
      align: 'start',
      spacing: '10px',
      font: FONTS.normal,
      palette: { text: COLORS.onLight[400] }
    },
    error: { spacing: '10px', font: FONTS.normal, palette: { text: COLORS.onLight[400] } }
  },

  elevations: {
    1: { x: '0px', y: '2px', spread: '0px', blur: '24px', color: 'rgba(4, 30, 66, 0.04)' },
    2: { x: '0px', y: '15px', spread: '0px', blur: '30px', color: 'rgba(4, 30, 66, 0.06)' }
  }
} as const

export const {
  Row,
  Spaced,
  SpacedMicro,
  Header,
  Header2,
  Header5,
  Paragraph,
  ParagraphSecondary,
  FieldSelect,
  Fill,
  Anchor,
  AnchorGolden,
  AnchorLarger
} = design(
  {
    FieldSelect: FactoryFieldSelect,
    Header: FactoryHeader,
    Paragraph: FactoryParagraph,
    Row: FactoryRow,
    Spaced: FactorySpaced,
    Fill: FactoryFill,
    Anchor: FactoryAnchor
  },
  GLOBALS,
  ({ global }) =>
    ({
      Row: { default: {} },
      Fill: { default: {} },
      Spaced: {
        default: { spacing: '16px' },
        SpacedMicro: { spacing: '8px' }
      },

      Anchor: {
        default: {
          font: GLOBALS.fonts.normal,
          palette: { text: global(['colors', 'hyperlinks', 500]) },
          $when: { active: {}, focus: {}, hover: {}, link: {}, nolink: {}, visited: {} }
        },

        AnchorGolden: {
          font: GLOBALS.fonts.normal,
          palette: { text: GLOBALS.colors.tangerineYellow[500] },
          $when: { active: {}, focus: {}, hover: {}, link: {}, nolink: {}, visited: {} }
        },

        AnchorLarger: {
          font: GLOBALS.fonts.larger,
          palette: { text: GLOBALS.colors.onLight[500] },
          $when: { active: {}, focus: {}, hover: {}, link: {}, nolink: {}, visited: {} }
        }
      },

      Header: {
        default: {
          tag: 'h1',
          font: GLOBALS.fonts.heading1,
          palette: { text: GLOBALS.colors.onLight[500] }
        },

        Header2: {
          tag: 'h2',
          font: GLOBALS.fonts.heading2,
          palette: { text: GLOBALS.colors.onLight[500] }
        },

        Header5: {
          tag: 'h5',
          font: GLOBALS.fonts.heading5,
          palette: { text: GLOBALS.colors.onLight[500] }
        }
      },

      Paragraph: {
        default: {
          font: GLOBALS.fonts.normal,
          palette: { text: GLOBALS.colors.onLight[500] }
        },

        ParagraphSecondary: {
          font: GLOBALS.fonts.normal,
          palette: { text: GLOBALS.colors.onLight[300] }
        }
      },

      FieldSelect: {
        default: {
          label: GLOBALS.field.label,
          error: GLOBALS.field.error,
          input: {
            angleSize: '18px',
            padding: GLOBALS.padding.default,
            font: GLOBALS.fonts.larger,
            border: {
              width: '2px',
              style: { top: 'none', left: 'none', bottom: 'solid', right: 'none' }
            },
            palette: {
              text: GLOBALS.colors.onLight[500],
              placeholder: GLOBALS.colors.onLight[500],
              background: GLOBALS.colors.onDark[500],
              border: GLOBALS.colors.onLight[500],
              angle: GLOBALS.colors.grey[500]
            }
          },
          tray: {
            offset: '0px',
            padding: { top: '0px', right: '0px', bottom: '0px', left: '0px' },
            border: {
              style: { top: 'none', left: 'solid', right: 'solid', bottom: 'solid' },
              width: '2px'
            },
            maxHeight: '295px',
            shadow: GLOBALS.elevations[2],
            palette: { background: 'white', border: GLOBALS.colors.onLight[500] }
          },
          trayItem: {
            padding: GLOBALS.padding.default,
            font: GLOBALS.fonts.larger,
            palette: {
              background: 'white',
              border: GLOBALS.colors.grey[200],
              text: GLOBALS.colors.onLight[500],
              textSelected: GLOBALS.colors.tangerineYellow[500]
            }
          },
          $when: {
            focus: { input: { palette: { border: GLOBALS.colors.onLight[500] } } },
            hover: {
              input: { palette: { border: GLOBALS.colors.onLight[500] } },
              trayItem: { palette: { text: GLOBALS.colors.tangerineYellow[500] } }
            },
            open: {
              input: {
                palette: { border: GLOBALS.colors.onDark[500] }
              }
            },
            active: {},
            readonly: {},
            invalid: { input: { palette: { border: GLOBALS.colors.grey[500] } } },
            disabled: {
              input: {
                palette: {
                  text: GLOBALS.colors.onLight[400],
                  background: GLOBALS.colors.grey[200],
                  border: GLOBALS.colors.grey[200]
                }
              }
            }
          }
        }
      }
    } as const)
)
